import { getRouteApi, Link, useRouter } from "@tanstack/react-router";
import {
  useDistributorEmployees,
  useEmployeeRoles,
  useUpdateFamily,
} from "creso-core/hooks";
import { getInitials, shouldEnableRBAC } from "creso-core/utils";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "web-design-system";
import { LucideUserKey } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Avatar, MemberAccessBadge, AvatarFamily } from "web-core";
import { useAuthContext } from "@/providers/auth-provider";
import { handleError } from "@/utils/error";
import { toast } from "sonner";
import { JOINT_CLIENT_HOLDING_PATTERNS } from "creso-core/constants";
import {
  AssignEmployeeSelect,
  SELECT_NO_EMPLOYEE_SENTINEL,
} from "../clients/assign-employee-select";
import { FamilyNotes } from "./family-notes";
import { FamilyTasks } from "./family-tasks";
import { FamilyContextMenu } from "./family-context-menu";

export function FamilyInfo() {
  const route = getRouteApi("/(auth)/_headed/clients/families/$id");
  const { family } = route.useLoaderData();
  const router = useRouter();
  const { user } = useAuthContext();

  const isRBACEnabled = useMemo(() => shouldEnableRBAC(user), [user]);
  const { data: employeesData } = useDistributorEmployees(
    { status: "active" },
    { enabled: isRBACEnabled }
  );
  const { data: employeeRoles } = useEmployeeRoles({
    enabled: isRBACEnabled,
  });
  const { mutateAsync: updateFamily, isPending: isAssigning } = useUpdateFamily(
    family?.id ?? 0
  );

  const members = family?.members ?? [];
  const selectedEmployeeId = family?.employee_id ?? null;
  const employees = employeesData?.results ?? [];

  const isActiveNow = useMemo(() => {
    if (!family?.last_seen) return false;
    const lastSeenDate = new Date(family?.last_seen);
    return Date.now() - lastSeenDate.getTime() < 2 * 60 * 1000;
  }, [family?.last_seen]);

  const roleIdToName = useMemo(() => {
    const map = new Map<number, string>();
    employeeRoles?.results?.forEach((role) => {
      map.set(Number(role.id), role.name);
    });
    return map;
  }, [employeeRoles?.results]);

  const handleAssignedToChange = useCallback(
    async (value: string) => {
      if (!family?.id) return;
      const nextEmployeeId =
        value === SELECT_NO_EMPLOYEE_SENTINEL ? null : Number(value);
      if (nextEmployeeId === selectedEmployeeId) return;

      try {
        await updateFamily({ assigned_to: nextEmployeeId });
        await router.invalidate();
        toast.success("Assigned to updated");
      } catch (error) {
        handleError(error, "Failed to update assignee");
      }
    },
    [family?.id, router, selectedEmployeeId, updateFamily]
  );

  return (
    <div className="flex flex-col">
      <Breadcrumb>
        <BreadcrumbList className="gap-0.5">
          <BreadcrumbItem className="text-xs">
            <Link className="text-muted-foreground" to="/clients/families">
              Families
            </Link>
          </BreadcrumbItem>
          {family?.name ? (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem className="text-primary text-xs">
                {family.name}
              </BreadcrumbItem>
            </>
          ) : null}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex flex-col mt-2 lg:mt-6 gap-6">
        <div className="flex flex-col gap-3 items-start">
          <div className="relative">
            <AvatarFamily
              fallback={getInitials(family?.name ?? "")}
              url=""
              size="lg"
              variant="gradient"
            />
            {isActiveNow && (
              <div className="size-2 bottom-0 absolute right-0 rounded-full bg-constructive" />
            )}
          </div>
          <div className="flex flex-col w-full">
            <div className="flex flex-row w-full items-center justify-between">
              <div className="text-lg font-semibold">{family?.name}</div>
              <FamilyContextMenu className="-mr-2" family={family} />
            </div>
            <span className="text-xs text-muted-foreground">
              {family?.member_count ?? members.length} Members
            </span>
          </div>
        </div>

        {members.map((member) => (
          <div key={member.client_id} className="flex flex-col gap-5">
            <Link
              to="/clients/$id"
              params={{ id: member.client_id.toString() }}
              className="text-sm truncate !no-underline hover:!no-underline"
              target="_blank"
            >
              <div className="flex items-center gap-3">
                <Avatar
                  fallback={getInitials(member.display_name ?? member.name)}
                  size="default"
                />
                <div className="flex flex-col gap-[2px] min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {member.display_name ?? member.name}
                    </div>
                    <MemberAccessBadge member={member} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {member.phone_number}
                    {member.holding_pattern &&
                    JOINT_CLIENT_HOLDING_PATTERNS.includes(
                      member.holding_pattern
                    )
                      ? ` • Joint`
                      : null}
                  </div>
                </div>
              </div>
            </Link>
          </div>
        ))}

        <div className="flex flex-col">
          {isRBACEnabled && employees.length > 0 ? (
            <div className="flex flex-row w-full items-center">
              <LucideUserKey className="size-4 text-muted-foreground shrink-0" />
              <span className="text-sm shrink-0 ml-3">Assigned to</span>
              <AssignEmployeeSelect
                employees={employees}
                roleIdToName={roleIdToName}
                assignedCountKind="family"
                value={
                  selectedEmployeeId == null
                    ? SELECT_NO_EMPLOYEE_SENTINEL
                    : String(selectedEmployeeId)
                }
                triggerLabel={
                  selectedEmployeeId != null
                    ? (employees.find(
                        (employee) => employee.id === selectedEmployeeId
                      )?.name ?? "No one")
                    : "No one"
                }
                triggerClassName="h-8 px-0 ml-1.5 gap-1 font-medium border-0 bg-transparent w-fit text-left"
                triggerTextClassName="text-sm line-clamp-1"
                disabled={isAssigning}
                onValueChange={handleAssignedToChange}
              />
            </div>
          ) : family?.employee_name ? (
            <div className="flex flex-col w-full gap-1">
              <span className="text-xs text-muted-foreground">Assigned to</span>
              <span className="text-sm">{family.employee_name}</span>
            </div>
          ) : null}

          {family ? (
            <>
              <div className="my-5 h-px w-full bg-border" />
              <div className="flex flex-col gap-8">
                <FamilyNotes family={family} />
                <FamilyTasks family={family} />
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
